import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import ContentWithSidebar from 'tailwind-components/ContentWithSidebar/ContentWithSidebar';
import SidebarContent from 'tailwind-components/ContentWithSidebar/SidebarContent';
import SidebarPlaceholder from 'tailwind-components/ContentWithSidebar/SidebarPlaceholder';
import useBelowBreakpoint from 'hooks/useBelowBreakpoint';
import useTabUrlState from 'hooks/useTabUrlState';
import ContentLibraryMainPanel from './ContentLibraryMainPanel';
import ContentLibraryPreviewPanel from './ContentLibraryPreviewPanel';
import {
  ALL,
  ALL_CATEGORY_ID,
  actionButtonLabel,
  buildCarrierList,
  categoryTabs,
  filterAssets,
  isDownloadType,
  isExternalType,
  materialTypeOptions,
  openExternal,
  tryDownloadToDevice,
  typesAvailableForCarrier
} from './contentLibraryUtils';

export default function ContentLibrary({
  getMarketingAssets,
  setLocalLoader,
  marketingAssets
}) {
  const list = Array.isArray(marketingAssets) ? marketingAssets : [];
  const dataLengthRef = useRef(0);
  dataLengthRef.current = list.length;

  const setLoaderGuarded = useCallback(
    loading => {
      if (loading && dataLengthRef.current > 0) return;
      setLocalLoader(loading);
    },
    [setLocalLoader]
  );

  useEffect(() => {
    const source = axios.CancelToken.source();
    getMarketingAssets(setLoaderGuarded, source);
    return () => {
      source.cancel('API Call Canceled');
    };
  }, [getMarketingAssets, setLoaderGuarded]);

  const isBelowLg = useBelowBreakpoint('lg');

  const [selectedCarrier, setSelectedCarrier] = useState(ALL);
  const [selectedType, setSelectedType] = useState(ALL);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedAsset, setSelectedAsset] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const categoryTabList = useMemo(() => categoryTabs(list), [list]);
  const categoryKeys = useMemo(
    () => categoryTabList.map(c => c.id),
    [categoryTabList]
  );
  // slug (URL id) → the raw value filterAssets matches on (label, or ALL for the "all" tab)
  const categoryValueBySlug = useMemo(() => {
    const m = {};
    categoryTabList.forEach(c => {
      m[c.id] = c.value;
    });
    return m;
  }, [categoryTabList]);
  const [selectedCategory, setSelectedCategory] = useTabUrlState({
    param: 'category',
    tabIds: categoryKeys,
    defaultTabId: ALL_CATEGORY_ID
  });
  const carrierList = useMemo(() => buildCarrierList(list), [list]);
  const allTypeLabels = useMemo(() => materialTypeOptions(list), [list]);

  const availableTypesForCarrier = useMemo(
    () => typesAvailableForCarrier(list, selectedCarrier),
    [list, selectedCarrier]
  );

  useEffect(() => {
    if (selectedType !== ALL && !availableTypesForCarrier.has(selectedType)) {
      setSelectedType(ALL);
    }
  }, [selectedCarrier, selectedType, availableTypesForCarrier]);

  useEffect(() => {
    // categories are dynamic (built from loaded assets) — don't scrub a deep-linked slug
    // until they arrive; once loaded, an unknown slug falls back to "all"
    if (list.length === 0) return;
    if (
      selectedCategory !== ALL_CATEGORY_ID &&
      !categoryKeys.includes(selectedCategory)
    ) {
      setSelectedCategory(ALL_CATEGORY_ID);
    }
  }, [list.length, categoryKeys, selectedCategory, setSelectedCategory]);

  const filtered = useMemo(
    () =>
      filterAssets(list, {
        category: categoryValueBySlug[selectedCategory] ?? ALL,
        carrier: selectedCarrier,
        type: selectedType,
        q: searchQuery
      }),
    [
      list,
      categoryValueBySlug,
      selectedCategory,
      selectedCarrier,
      selectedType,
      searchQuery
    ]
  );

  const selectAsset = useCallback(
    asset => {
      setSelectedAsset(asset);
      if (isBelowLg) setSidebarOpen(true);
    },
    [isBelowLg]
  );

  const closePreview = useCallback(() => {
    setSelectedAsset(null);
    setSidebarOpen(false);
  }, []);

  const onAssetAction = useCallback(() => {
    if (!selectedAsset) return;
    const url = selectedAsset.assetUrl;
    if (!url) return;
    if (isExternalType(selectedAsset.assetUrlType)) {
      openExternal(url);
    } else if (isDownloadType(selectedAsset.assetUrlType)) {
      void tryDownloadToDevice(url);
    } else {
      openExternal(url);
    }
  }, [selectedAsset]);

  const actionLabel = useMemo(
    () => actionButtonLabel(selectedAsset),
    [selectedAsset]
  );

  const canAct = Boolean(selectedAsset?.assetUrl);
  const searchFieldId = 'content-library-search';

  const onCarrierChange = useCallback(v => {
    setSelectedCarrier(v);
    setSelectedType(ALL);
  }, []);

  const mainContent = (
    <ContentLibraryMainPanel
      categories={categoryTabList}
      selectedCategory={selectedCategory}
      onCategoryChange={setSelectedCategory}
      carrierList={carrierList}
      selectedCarrier={selectedCarrier}
      onCarrierChange={onCarrierChange}
      allTypeLabels={allTypeLabels}
      selectedType={selectedType}
      onTypeChange={setSelectedType}
      searchFieldId={searchFieldId}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      filteredAssets={filtered}
      selectedAsset={selectedAsset}
      onSelectAsset={selectAsset}
    />
  );

  const sidebarContent = selectedAsset ? (
    <SidebarContent showScrollbar includesHeader>
      <ContentLibraryPreviewPanel
        selectedAsset={selectedAsset}
        actionLabel={actionLabel}
        canAct={canAct}
        onAssetAction={onAssetAction}
      />
    </SidebarContent>
  ) : (
    <SidebarPlaceholder text='Select a piece of content to see a preview' />
  );

  return (
    <div className='h-full w-full min-h-0'>
      <ContentWithSidebar
        mainContent={mainContent}
        sidebarContent={sidebarContent}
        sidebarAsSheetOnMobile
        sidebarOpen={isBelowLg ? sidebarOpen : Boolean(selectedAsset)}
        onSidebarOpenChange={setSidebarOpen}
        sidebarTitle='Preview'
        handleCloseSidebar={closePreview}
        desktopHeaderClassName='bg-white rounded-t-xl'
      />
    </div>
  );
}

const carrierShape = PropTypes.shape({
  name: PropTypes.string,
  id: PropTypes.string
});

const assetShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  type: PropTypes.string,
  name: PropTypes.string,
  carrier: carrierShape,
  categories: PropTypes.arrayOf(PropTypes.string),
  previewImgUrl: PropTypes.string,
  assetUrl: PropTypes.string,
  assetUrlType: PropTypes.string
});

ContentLibrary.propTypes = {
  getMarketingAssets: PropTypes.func.isRequired,
  setLocalLoader: PropTypes.func.isRequired,
  marketingAssets: PropTypes.arrayOf(assetShape)
};

ContentLibrary.defaultProps = {
  marketingAssets: []
};
